import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { launchSourceLabel, liveDuration, shortId, statusClass, statusDot, statusLabel } from '../tracking-format.util';
import type { TrackingSession } from '../tracking-demo.models';

export type SessionAction = 'view' | 'screen' | 'join' | 'disconnect' | 'blacklist' | 'participants';

/** The real-time live sessions table (ticking duration, row-click, per-row admin actions). */
@Component({
  selector: 'app-live-session-table',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './live-session-table.component.html',
})
export class LiveSessionTableComponent {
  @Input() sessions: TrackingSession[] = [];
  @Input() now = 0;
  /** False when this product panel has no public demo call page to observer-join into. */
  @Input() joinEnabled = true;
  @Output() rowClick = new EventEmitter<TrackingSession>();
  @Output() action = new EventEmitter<{ action: SessionAction; session: TrackingSession }>();

  readonly liveDuration = liveDuration;
  readonly launchSourceLabel = launchSourceLabel;
  readonly shortId = shortId;
  readonly statusClass = statusClass;
  readonly statusDot = statusDot;
  readonly statusLabel = statusLabel;

  location(s: TrackingSession): string {
    const sec = s.security;
    if (!sec) return '—';
    return [sec.city, sec.country].filter(Boolean).join(', ') || '—';
  }

  isLive(s: TrackingSession): boolean {
    return ['waiting', 'connecting', 'live', 'on_hold'].includes(s.status);
  }

  /** Observer join needs a WebRTC room — solo demos have none, so minting a token always
   *  409s — and a product with no public call page configured (joinEnabled=false). */
  canJoin(s: TrackingSession): boolean {
    return this.joinEnabled && this.isLive(s) && s.kind === 'room';
  }

  emit(action: SessionAction, session: TrackingSession, ev: Event): void {
    ev.stopPropagation();
    this.action.emit({ action, session });
  }
}
